<template>
  <!--
    Rendu unifié d'UN champ de configuration d'intégration (LOT G, G7).
    Consommé À LA FOIS par le mode simple ET le mode wizard de IntegrationConfig,
    ce qui supprime par construction la divergence historique entre les deux
    (multiselect, champs remote, validation/fieldErrors identiques partout).

    Le composant est autonome : il localise lui-même label/placeholder/aide/options
    via `pickLocalizedText`, gère les 9 types de champs, l'état remote
    (chargement / erreur + bouton réessayer) et l'affichage d'erreur/aide.
  -->
  <div class="form-group">
    <label
      :for="`field-${field.key}`"
      class="form-label"
    >
      {{ label }}
      <span
        v-if="field.required"
        class="form-required"
      >*</span>
    </label>

    <!-- Text / Email / URL -->
    <InputText
      v-if="['text', 'email', 'url'].includes(field.type)"
      :id="`field-${field.key}`"
      :model-value="modelValue"
      :type="field.type === 'text' ? undefined : field.type"
      :placeholder="placeholder"
      :required="field.required"
      :minlength="field.validation?.minLength"
      :maxlength="field.validation?.maxLength"
      :pattern="field.validation?.pattern"
      class="form-control"
      :class="{ 'p-invalid': fieldError }"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <!-- Password -->
    <Password
      v-else-if="field.type === 'password'"
      :id="`field-${field.key}`"
      :model-value="modelValue"
      :placeholder="placeholder"
      :required="field.required"
      :feedback="false"
      toggle-mask
      class="form-control"
      :class="{ 'p-invalid': fieldError }"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <!-- Number -->
    <InputNumber
      v-else-if="field.type === 'number'"
      :id="`field-${field.key}`"
      :model-value="modelValue"
      :placeholder="placeholder"
      :required="field.required"
      :min="field.validation?.min"
      :max="field.validation?.max"
      :step="field.validation?.step"
      class="form-control"
      :class="{ 'p-invalid': fieldError }"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <!-- Select -->
    <div v-else-if="field.type === 'select'">
      <div
        v-if="field.remote && remoteLoading"
        class="remote-placeholder"
      >
        <i class="fas fa-spinner fa-spin" />
        {{ t('integrations.config.loadingRemoteData') }}
      </div>
      <div
        v-else-if="field.remote && remoteError"
        class="remote-error"
      >
        <i class="fas fa-exclamation-circle" />
        {{ t('integrations.config.remoteDataError') }}
        <button
          type="button"
          class="remote-error__retry"
          @click="$emit('remote-retry', field)"
        >
          <i class="fas fa-rotate-right" />
          {{ t('integrations.config.retry') }}
        </button>
      </div>
      <Dropdown
        v-else
        :id="`field-${field.key}`"
        :model-value="modelValue"
        :options="resolvedOptions"
        option-label="label"
        option-value="value"
        :placeholder="placeholder"
        :required="field.required"
        class="form-control"
        :class="{ 'p-invalid': fieldError }"
        @update:model-value="$emit('update:modelValue', $event)"
      />
    </div>

    <!-- MultiSelect -->
    <div v-else-if="field.type === 'multiselect'">
      <div
        v-if="field.remote && remoteLoading"
        class="remote-placeholder"
      >
        <i class="fas fa-spinner fa-spin" />
        {{ t('integrations.config.loadingRemoteData') }}
      </div>
      <div
        v-else-if="field.remote && remoteError"
        class="remote-error"
      >
        <i class="fas fa-exclamation-circle" />
        {{ t('integrations.config.remoteDataError') }}
        <button
          type="button"
          class="remote-error__retry"
          @click="$emit('remote-retry', field)"
        >
          <i class="fas fa-rotate-right" />
          {{ t('integrations.config.retry') }}
        </button>
      </div>
      <MultiSelect
        v-else
        :id="`field-${field.key}`"
        :model-value="modelValue"
        :options="resolvedOptions"
        option-label="label"
        option-value="value"
        :placeholder="placeholder"
        class="form-control"
        :class="{ 'p-invalid': fieldError }"
        @update:model-value="$emit('update:modelValue', $event)"
      />
    </div>

    <!-- Checkbox -->
    <div
      v-else-if="field.type === 'checkbox'"
      class="form-switch"
    >
      <InputSwitch
        :id="`field-${field.key}`"
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
      />
    </div>

    <!-- Textarea -->
    <Textarea
      v-else-if="field.type === 'textarea'"
      :id="`field-${field.key}`"
      :model-value="modelValue"
      :placeholder="placeholder"
      :required="field.required"
      :rows="field.validation?.rows || 4"
      :maxlength="field.validation?.maxLength"
      class="form-control"
      :class="{ 'p-invalid': fieldError }"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <small
      v-if="fieldError"
      class="form-error"
    >{{ fieldError }}</small>
    <small
      v-else-if="helpText"
      class="form-hint"
    >{{ helpText }}</small>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputSwitch from 'primevue/inputswitch';
import Textarea from 'primevue/textarea';
import { pickLocalizedText } from '@/utils/localized-text';
import type { ConfigField } from '@/composables/useIntegrations';

interface Option { value: string; label: string }

export default defineComponent({
  name: 'ConfigFieldRenderer',

  components: {
    InputText,
    Password,
    InputNumber,
    Dropdown,
    MultiSelect,
    InputSwitch,
    Textarea,
  },

  props: {
    field: { type: Object as PropType<ConfigField>, required: true },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modelValue: { type: null as unknown as PropType<any>, default: undefined },
    /** Message d'erreur de validation pour ce champ (vide = aucune erreur). */
    fieldError: { type: String, default: '' },
    /** État remote (uniquement pertinent si `field.remote`). */
    remoteLoading: { type: Boolean, default: false },
    remoteError: { type: Boolean, default: false },
    remoteOptions: { type: Array as PropType<Option[]>, default: () => [] },
  },

  emits: ['update:modelValue', 'remote-retry'],

  setup(props) {
    const { t, locale } = useI18n();

    const localize = (
      value: string | Record<string, string> | undefined,
    ): string => pickLocalizedText(value as any, locale.value);

    const label = computed(() => localize(props.field.label) || props.field.key);
    const placeholder = computed(() => localize(props.field.placeholder));
    const helpText = computed(() => localize(props.field.helpText));

    // Options remote (si champ remote) sinon options statiques du schéma.
    const resolvedOptions = computed<Option[]>(() => {
      if (props.field.remote) return props.remoteOptions || [];
      return (props.field.options || []).map((o) => ({
        value: o.value,
        label: localize(o.label) || o.value,
      }));
    });

    return {
      t,
      label,
      placeholder,
      helpText,
      resolvedOptions,
    };
  },
});
</script>

<style scoped lang="scss">
/* Styles de champ — dupliqués depuis IntegrationConfig pour que le rendu soit
   identique quel que soit le mode (le scope Vue ne traverse pas les composants). */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.form-required {
  color: #dc2626;
  margin-left: 0.125rem;
}

.form-control {
  width: 100%;
}

:deep(.p-password) {
  width: 100%;
  .p-password-input, input { width: 100%; }
}

.form-switch {
  padding: 0.375rem 0;
}

.form-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.4;
}

.form-error {
  font-size: 0.75rem;
  color: #dc2626;
  line-height: 1.4;
}

:deep(.p-invalid) {
  border-color: #dc2626 !important;
}

.remote-placeholder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #9ca3af;
}

.remote-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #dc2626;
}

.remote-error__retry {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #94c840;
    color: #5d8a1e;
  }
}
</style>
